import { isEmpty, isEqual, isNil } from 'lodash'

const blank = (value) => isNil(value) || value === ''

// Numeric fields arrive as numbers from a saved prompt and as strings from a form the user has typed into, so "5"
// and 5 have to compare equal. An unparseable value becomes NaN, which compares unequal to everything — the safe
// direction, since it forces a new prompt row rather than reusing one on a guess.
const toNumber = (value) => (blank(value) ? null : Number(value))

/**
 * The fields that make a prompt what it is, normalised so the same prompt always produces the same object.
 *
 * Applied to BOTH sides of a comparison — the form's values and the criterion's saved prompt — because those two
 * carry the same information in different shapes: the form holds `''` and `'true'` where the saved row holds
 * `null` and `true`. Comparing them raw reports every prompt as edited.
 *
 * These are exactly the fields `savePromptForOptimization` writes. Anything not listed here does not affect what
 * the model sees, so changing it should not force a new prompt row.
 */
export const promptFingerprint = (source = {}) => ({
  user_prompt: source.user_prompt || '',
  system_prompt: source.system_prompt || '',
  first_x_minutes: toNumber(source.first_x_minutes),
  last_x_minutes: toNumber(source.last_x_minutes),
  // 'both' is the form's own default for an unset side, so an untouched prompt matches a saved one that never set it.
  side: source.side || 'both',
  include_timestamps: source.include_timestamps === true || source.include_timestamps === 'true',
  silence_threshold_seconds: toNumber(source.silence_threshold_seconds),
  llm_model_id: toNumber(source.llm_model_id),
  max_response_tokens: toNumber(source.max_response_tokens),
})

// The fallback trigger scores, as a comparable list. The form holds select options ({value, label, id}) where the
// criterion holds bare strings, and neither promises an order.
const fallbackScores = (source = {}) =>
  (source.scores_to_fallback || []).map((score) => score?.value ?? score).sort()

/**
 * The fallback configuration, which lives on the CRITERION rather than the prompt row but still changes what a run
 * measures: with fallback, production answers a call with a cheap model first and escalates only on certain
 * answers, so its stored answers are a two-model mix.
 *
 * The fallback MODEL only means anything when there are trigger scores — mirroring the criterion save, which nulls
 * it out otherwise. Without that, every criterion with no fallback would compare unequal, because the form defaults
 * the model to a real id while the saved criterion holds null.
 */
export const fallbackFingerprint = (source = {}) => {
  const scores = fallbackScores(source)

  return {
    scores_to_fallback: scores,
    fallback_initial_model_id: isEmpty(scores) ? null : toNumber(source.fallback_initial_model_id),
  }
}

/** Whether this criterion answers calls through a fallback model in production. */
export const usesFallback = (source = {}) => !isEmpty(fallbackScores(source))

/**
 * Whether a run from `GET /optimize/pending` belongs to the criterion currently on screen.
 *
 * By LINEAGE first. `scorecard_criteria` is insert-only, so `id` is the version and is re-minted on every save
 * while `scid` identifies the criterion across all of them — and a criterion that has never been saved has a
 * `scid` (generated client-side by `addTemporaryCriteriaHelper`) but no id at all.
 *
 * ⚠ Do NOT "fix" an unmatched unsaved criterion by treating two blank ids as equal: every unsaved criterion on a
 * scorecard has a null id, so they would all match each other's runs.
 *
 * `criterionId` is the fallback for runs started outside the button — the CLI takes a criterion id and sends no
 * draft — which would otherwise never be visible here.
 */
export const matchesCriterion = (run = {}, criteria = {}) => {
  const runScid = run.criterionDraft?.scid

  if (!isNil(runScid) && !isNil(criteria?.scid)) {
    return runScid === criteria.scid
  }

  return !isNil(criteria?.id) && run.criterionId === criteria.id
}

/**
 * Whether the run can measure the criterion's OWN prompt record instead of saving a new one.
 *
 * Worth asking because a record created a second ago has never scored a call, so production's stored answers can
 * never be reused against it — the run pays to re-answer every call and then reports that re-run as "your prompt
 * now", which is an approximation of production rather than production.
 *
 * It asks about more than the prompt row: fallback lives on the criterion and changes how production's answers
 * were produced.
 *
 * ⚠ FALSE POSITIVES ARE THE DANGEROUS DIRECTION — calling an edited prompt unchanged would score answers to
 * different text as though they came from what is on screen. So this answers true only with a saved record to
 * point at and every identity-bearing field matching; anything missing or unparseable falls through to saving a
 * new record, which is always correct if wasteful.
 */
export const canReuseCriterionPrompt = (values, savedPrompt, savedCriteria) => {
  if (isNil(savedPrompt?.id)) {
    return false
  }

  return (
    isEqual(promptFingerprint(values), promptFingerprint(savedPrompt)) &&
    isEqual(fallbackFingerprint(values), fallbackFingerprint(savedCriteria))
  )
}
